/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { PLAN_DEFAULTS } from '../../../shared/features';
import type { CapabilityValue, FeatureKey, ResolvedCapabilities } from '../../../shared/features';
import { useI18n } from '../../i18n/I18nProvider';

const CapabilityContext = createContext<ResolvedCapabilities>(PLAN_DEFAULTS.founder);

export function CapabilityProvider({ capabilities, children }: { capabilities?: ResolvedCapabilities; children: ReactNode }) {
  return <CapabilityContext.Provider value={capabilities || PLAN_DEFAULTS.founder}>{children}</CapabilityContext.Provider>;
}

export function useCapabilities(): ResolvedCapabilities {
  return useContext(CapabilityContext);
}

export interface CapabilityState extends CapabilityValue {
  isEnabled: boolean;
  isHidden: boolean;
  isLocked: boolean;
}

export function capabilityState(capability: CapabilityValue): CapabilityState {
  const isHidden = capability.visibility === 'hidden';
  const isLocked = capability.visibility === 'locked' || (!capability.enabled && !isHidden);
  return { ...capability, isEnabled: capability.enabled && !isHidden && !isLocked, isHidden, isLocked };
}

export function useCapability(feature: FeatureKey): CapabilityState {
  return capabilityState(useCapabilities()[feature]);
}

export function FeatureGate({ feature, children, locked }: { feature: FeatureKey; children: ReactNode; locked?: ReactNode }) {
  const capability = useCapability(feature);
  if (capability.isHidden) return null;
  if (!capability.isEnabled) return capability.isLocked ? <>{locked || null}</> : null;
  return <>{children}</>;
}

export const Capability = FeatureGate;

export function LockedFeature({ title, owner = false, compact = false }: { title: string; owner?: boolean; compact?: boolean }) {
  const { t } = useI18n();
  return <div className={`locked-feature${compact ? ' locked-feature--compact' : ''}`} role="note">
    <strong>{title} ★</strong>
    <span>{t('capability.otherPlan')}</span>
    {owner && <a href="https://t.me/walldstudio" target="_blank" rel="noreferrer">{t('capability.ownerContact')} @walldstudio</a>}
  </div>;
}
